import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { ACTIVE_CITIES } from '@/lib/site';
import { useCountUp } from '@/hooks/useCountUp';
import { MapPin } from 'lucide-react';

// Stylised India outline path (simplified, decorative — not survey accurate)
const INDIA_PATH =
  'M180 30 C200 28 230 30 260 38 C290 46 320 60 350 78 C380 96 410 120 420 150 C430 180 420 215 410 240 C400 265 390 290 400 320 C410 350 410 380 380 410 C350 440 310 470 270 480 C230 490 200 480 180 460 C160 440 140 410 130 380 C120 350 110 320 110 290 C110 260 105 230 100 200 C95 170 110 130 130 90 C150 60 160 35 180 30 Z';

function MarkerDot({ city, hovered, setHovered }) {
  const isPrimary = !!city.primary;
  return (
    <motion.div
      data-testid={`map-marker-${city.name.toLowerCase()}`}
      onMouseEnter={() => setHovered(city.name)}
      onMouseLeave={() => setHovered(null)}
      style={{ left: `${city.x}%`, top: `${city.y}%` }}
      initial={{ scale: 0, opacity: 0 }}
      whileInView={{ scale: 1, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ delay: 0.2 + Math.random() * 0.4, duration: 0.5 }}
      className="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer"
    >
      <span
        className={`block h-3 w-3 rounded-full ${
          isPrimary ? 'bg-saffron-500' : 'bg-maroon-500'
        } ring-4 ring-saffron-500/20 relative`}
      >
        {isPrimary && (
          <span className="absolute inset-0 rounded-full bg-saffron-500 animate-dot-ping" />
        )}
      </span>
      {(isPrimary || hovered === city.name) && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-maroon-700 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-white shadow-lg"
        >
          {city.name}
          {city.note && (
            <span className="ml-1 text-saffron-200/80 normal-case font-normal">
              · {city.note}
            </span>
          )}
        </motion.div>
      )}
    </motion.div>
  );
}

function ImpactCounter({ value, suffix, label, when }) {
  const v = useCountUp(value, { when, duration: 2000 });
  return (
    <div>
      <div className="font-display text-4xl md:text-5xl font-bold text-maroon-700 dark:text-saffron-200">
        {v.toLocaleString('en-IN')}
        <span className="text-saffron-500">{suffix}</span>
      </div>
      <div className="mt-1 text-xs uppercase tracking-[0.2em] text-foreground/60">{label}</div>
    </div>
  );
}

export default function Impact() {
  const [hovered, setHovered] = useState(null);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="impact"
      data-testid="impact-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="container-x">
        <div className="text-center max-w-3xl mx-auto">
          <p className="eyebrow">Our Impact</p>
          <h2 className="h-display mt-4 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] text-maroon-700 dark:text-foreground">
            Footprints across <span className="italic font-serif saffron-text">Bharat.</span>
          </h2>
          <p className="mt-5 text-foreground/65 leading-relaxed">
            We are headquartered in Delhi, with active hubs in Lucknow and Nagpur — but our
            volunteers and field partners reach communities in every corner of India.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" ref={ref}>
          {/* India map */}
          <div className="lg:col-span-7">
            <div className="relative aspect-[5/6] rounded-3xl bg-gradient-to-br from-saffron-50 via-cream to-saffron-50 dark:from-maroon-900/40 dark:via-[#1F1218] dark:to-maroon-900/40 border border-border/70 overflow-hidden grain">
              <div className="absolute inset-0 bg-mandala bg-center bg-no-repeat opacity-20 animate-slow-spin" />
              <svg viewBox="0 0 500 540" className="absolute inset-0 h-full w-full p-6 md:p-12">
                <defs>
                  <linearGradient id="indiaG" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#FFD7B5" />
                    <stop offset="100%" stopColor="#FF8C45" />
                  </linearGradient>
                  <linearGradient id="indiaStroke" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#FF671F" />
                    <stop offset="100%" stopColor="#6B1A24" />
                  </linearGradient>
                </defs>
                <motion.path
                  d={INDIA_PATH}
                  fill="url(#indiaG)"
                  fillOpacity="0.55"
                  stroke="url(#indiaStroke)"
                  strokeWidth="2"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 2, ease: 'easeInOut' }}
                />
              </svg>

              {/* Markers overlay */}
              <div className="absolute inset-0 p-6 md:p-12">
                <div className="relative h-full w-full">
                  {ACTIVE_CITIES.map((c) => (
                    <MarkerDot
                      key={c.name}
                      city={c}
                      hovered={hovered}
                      setHovered={setHovered}
                    />
                  ))}
                </div>
              </div>

              {/* Legend */}
              <div className="absolute left-4 bottom-4 flex flex-col gap-1.5 rounded-xl glass-strong px-3 py-2.5 text-[11px] font-medium">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-saffron-500" />
                  Active hub city
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-maroon-500" />
                  Field operations
                </div>
              </div>
            </div>
          </div>

          {/* Right: counters + stories */}
          <div className="lg:col-span-5 space-y-8">
            <div className="rounded-3xl glass p-7">
              <p className="eyebrow flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5" /> Active Presence
              </p>
              <h3 className="h-display mt-3 text-2xl font-semibold text-foreground">
                Delhi · Lucknow · Nagpur
              </h3>
              <p className="mt-2 text-sm text-foreground/65">
                Three hub cities. One vow. Operations supporting families across 14 states and
                growing every season.
              </p>
              <div className="mt-7 grid grid-cols-2 gap-6">
                <ImpactCounter value={80} suffix="+" label="Field Camps" when={inView} />
                <ImpactCounter value={88} suffix="%" label="Aid to Programs" when={inView} />
                <ImpactCounter value={6} suffix="" label="States Served" when={inView} />
                <ImpactCounter value={8} suffix="" label="Avg. Camps / Mo." when={inView} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
